import { Radio as AntRadio, RadioProps as AntRadioProps, RadioGroupProps as AntRadioGroupProps } from 'antd'

export interface RadioProps extends AntRadioProps {
  /**
   * Radio label
   */
  label?: string
  /**
   * Size of the radio text
   * @default 'small'
   */
  size?: 'small' | 'default'
}

/**
 * Radio Component
 *
 * A radio input with optional label.
 *
 * @example
 * <RadioGroup value={value} onChange={onChange}>
 *   <Radio value="option1">Option 1</Radio>
 *   <Radio value="option2">Option 2</Radio>
 * </RadioGroup>
 *
 * @example
 * // Radio with label prop
 * <Radio label="Select this option" value="option1" />
 */
export function Radio({ label, children, size = 'small', className, ...props }: RadioProps) {
  const sizeClass = size === 'small' ? 'text-sm' : ''
  const combinedClass = [sizeClass, className].filter(Boolean).join(' ')

  return <AntRadio className={combinedClass || undefined} {...props}>{label || children}</AntRadio>
}

export interface RadioGroupProps extends Omit<AntRadioGroupProps, 'size'> {
  /**
   * Size of the radio text
   * @default 'small'
   */
  size?: 'small' | 'middle' | 'large'
}

/**
 * RadioGroup Component
 *
 * A group of radio buttons with optional size control.
 *
 * @example
 * <RadioGroup
 *   options={[{ label: 'Option 1', value: '1' }, { label: 'Option 2', value: '2' }]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 *   size="small"
 * />
 */
export function RadioGroup({ size = 'small', className, ...props }: RadioGroupProps) {
  const sizeClass = size === 'small' ? '[&_.ant-radio-wrapper]:text-sm' : ''
  const combinedClass = [sizeClass, className].filter(Boolean).join(' ')

  return <AntRadio.Group size={size} className={combinedClass || undefined} {...props} />
}

/**
 * RadioButton Component
 *
 * A button-style radio input, useful for toolbar-like selections.
 */
export const RadioButton = AntRadio.Button
